// frontend/src/pages/lobby/LobbyPacketPage.tsx
import { useCallback, useState } from 'react'
import { Box, Button, Flex, Heading, HStack, IconButton, Spinner, Text, useDisclosure } from '@chakra-ui/react';
import { FiArrowLeft } from 'react-icons/fi'
import { useNavigate, useParams } from 'react-router-dom'
import { useAttendeePacket } from '../../hooks/useLobbyDay'
import { useAttendeeSession } from '../../hooks/useAttendeeSession'
import { PacketContent } from '../../components/lobbyDay/PacketContent'
import { lobbyAttendeeApi } from '../../lib/api'
import { AttendeeOutcomeSheet, type AttendeeOutcomeInput } from '../../components/lobby/AttendeeOutcomeSheet'
import { AttendeeDeadEnd } from '../../components/lobby/AttendeeDeadEnd'

export function LobbyPacketPage() {
  const { token, meetingId } = useParams<{ token: string; meetingId: string }>()
  const navigate = useNavigate()
  const { data: packet, isLoading, error } = useAttendeePacket(token, meetingId)
  const outcomeSheet = useDisclosure()
  // LW-16: once the attendee records, close the sheet and flip the entry button to "Edit outcome"
  // so re-opening it reads as an edit. Reload persistence (LWFS-3): the packet DTO now carries the
  // attendee's own prior response, so a previously-saved outcome flips the button to "Edit outcome"
  // on a fresh load too, not only within the recording session.
  const [recordedThisSession, setRecordedThisSession] = useState(false)
  const onRecorded = useCallback(() => {
    setRecordedThisSession(true)
    outcomeSheet.onClose()
  }, [outcomeSheet])
  const session = useAttendeeSession(token, onRecorded)
  const hasRecorded = recordedThisSession
    || (!!packet && packet.attendeeResponse !== 'NotRecorded')

  if (isLoading) return <Flex minH="100vh" align="center" justify="center"><Spinner size="lg" color="accent" /></Flex>
  if (!!error || !packet) return <AttendeeDeadEnd currentToken={token} />

  return (
    <Box minH="100vh" bg="gray.50">
      <HStack bg="band" color="white" px={2} py={2} gap={2}>
        <IconButton
          aria-label="Back to schedule"
          size="sm"
          variant="ghost"
          color="white"
          onClick={() => navigate(`/lobby/${token}`)}><FiArrowLeft /></IconButton>
        <Box>
          <Heading size="sm">{packet.legislatorName}</Heading>
          <Text fontSize="xs" opacity={0.8}>
            {packet.startTime.slice(0, 5)}{packet.room ? ` · Rm ${packet.room}` : ''} · {packet.districtLabel}
          </Text>
        </Box>
      </HStack>
      <Box p={3} maxW="480px" mx="auto" bg="canvas" minH="calc(100vh - 56px)">
        {packet.coAttendees.length > 0 && (
          <Text fontSize="sm" color="gray.600" mb={2}>With {packet.coAttendees.join(', ')}</Text>
        )}
        <PacketContent
          packet={packet}
          attachmentHref={(id) => lobbyAttendeeApi.attachmentUrl(token!, id)}
        />
        <Button
          mt={4}
          w="full"
          minH="44px"
          colorPalette="brand"
          onClick={outcomeSheet.onOpen}
        >
          {hasRecorded ? 'Edit outcome' : 'Record outcome'}
        </Button>
      </Box>
      <AttendeeOutcomeSheet
        isOpen={outcomeSheet.open}
        onClose={outcomeSheet.onClose}
        onSubmit={(input: AttendeeOutcomeInput) =>
          meetingId && session.recordOutcome({ meetingId, response: input.response, notes: input.notes })}
        onAcceptTerms={(input: AttendeeOutcomeInput) =>
          meetingId && session.acceptTermsAndRecord({ meetingId, response: input.response, notes: input.notes })}
        termsRequired={session.termsRequired}
        unavailable={session.unavailable}
        isSubmitting={session.isRecording}
        hasExistingOutcome={hasRecorded}
      />
    </Box>
  );
}
